<script lang="ts">
  import { onMount } from 'svelte';
  import type { Component } from 'svelte';
  import { app, disposeAppState, initializeApp, undoDelete } from './lib/app-state.svelte';
  import { canOfferUpdate } from './lib/update-gate';

  let updateServiceWorker: ((reloadPage?: boolean) => Promise<void>) | undefined;
  type ViewComponent = Component<Record<string, never>>;
  const viewPromises = new Map<string, Promise<ViewComponent>>();
  const routeTitles: Record<string, string> = { today: 'Today', week: 'This Week', calendar: 'Calendar', workout: 'Training', progress: 'Progress', library: 'Training Library', settings: 'Settings' };
  let previousRoute: string | undefined;
  const lazyView = (name: string, loader: () => Promise<{ default: ViewComponent }>) => {
    if (!viewPromises.has(name)) viewPromises.set(name, loader().then(module => module.default));
    return viewPromises.get(name)!;
  };

  function routeView(route: string): Promise<ViewComponent> {
    return route === 'today' ? lazyView('today', () => import('./components/TodayView.svelte')) : route === 'week' ? lazyView('week', () => import('./components/WeekView.svelte')) : route === 'calendar' ? lazyView('calendar', () => import('./components/CalendarView.svelte')) : route === 'workout' ? lazyView('workout', () => import('./components/WorkoutView.svelte')) : route === 'progress' ? lazyView('progress', () => import('./components/ProgressView.svelte')) : route === 'library' ? lazyView('library', () => import('./components/LibraryView.svelte')) : lazyView('settings', () => import('./components/SettingsView.svelte'));
  }

  function focusRouteHeading(route: string, attempt = 0) {
    requestAnimationFrame(() => {
      if (app.route !== route) return;
      const heading = document.querySelector<HTMLElement>('.main-content h1');
      if (!heading && attempt < 30) return focusRouteHeading(route, attempt + 1);
      heading?.setAttribute('tabindex', '-1');
      heading?.focus();
    });
  }

  $effect(() => {
    if (!app.ready) return;
    const route = app.route;
    document.title = `${routeTitles[route] || 'Sampson Swole Elite'}: Sampson Swole Elite`;
    if (previousRoute && previousRoute !== route) focusRouteHeading(route);
    previousRoute = route;
  });

  onMount(() => {
    document.getElementById('haptic-switch')?.setAttribute('switch', '');
    void initializeApp();
    void import('virtual:pwa-register').then(({ registerSW }) => {
      updateServiceWorker = registerSW({ onNeedRefresh() { app.updateReady = true; }, onOfflineReady() { app.syncStatus = 'Offline mode ready'; } });
    });
    const clearBadge = () => {
      (navigator as Navigator & { clearAppBadge?: () => Promise<void> }).clearAppBadge?.();
      navigator.serviceWorker.controller?.postMessage({ type: 'CLEAR_APP_BADGE' });
    };
    window.addEventListener('focus', clearBadge);
    return () => { window.removeEventListener('focus', clearBadge); disposeAppState(); };
  });
</script>

<svelte:head><title>Sampson Swole Academy Elite</title><meta name="description" content="Private, offline-first workout tracking for Sampson Swole Academy." /></svelte:head>

{#if !app.ready}
  <main class="loading-screen"><span class="brand-mark large"><i></i></span><p class="eyebrow">Sampson Swole Academy</p><h1>Preparing your training floor</h1><div class="loading-line"><span></span></div></main>
{:else}
  <div class="app-shell">
    {#await import('./components/Navigation.svelte') then module}<module.default />{/await}
    <main class="main-content">
      {#await routeView(app.route)}<div class="route-loading" role="status">Opening view...</div>{:then View}<View />{/await}
    </main>
    {#if app.activeSession}
      {#await import('./components/ActiveWorkoutBar.svelte') then module}<module.default />{/await}
    {/if}
  </div>
{/if}

<input id="haptic-switch" class="haptic-switch" type="checkbox" aria-hidden="true" tabindex="-1" />

{#if app.toast}<div class="toast" role="status"><span>{app.toast}</span>{#if app.toastAction === 'undo'}<button onclick={undoDelete}>Undo</button>{/if}</div>{/if}
{#if canOfferUpdate(app.updateReady, app.activeSession)}<aside class="update-toast"><span aria-hidden="true">↻</span><div><strong>Elite update ready</strong><small>Reload to use the newest version.</small></div><button onclick={() => updateServiceWorker?.(true)}>Update</button></aside>{/if}

{#if app.lastSummary}
  {#await import('./components/WorkoutSummary.svelte') then module}<module.default />{/await}
{/if}
